import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectWidgetEffectiveCols } from '../../redux/datasourceSlice.js';
import DatasourceAddSettingRow from './DatasourceAddSettingRow.jsx';
import Backend from 'react-dnd-html5-backend';
import { DragHandle, DropArea } from '../../../common/DragNDrop.jsx';
import { DndProvider } from 'react-dnd';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import { cloneDeep } from 'lodash';
import { FaIcon } from '../../../common/Icons.jsx';
import {closeDetail, openDetail, saveWidgetSettings } from '../../redux/dashboardSlice.js';
import styles from '../common/ListGroupItem.module.scss';
import DatasourceSettingsRow from './DatasourceSettingsRow.jsx';
import DetailSidebarSettings, { SIDEBAR_SETTING_TYPES } from '../DetailSidebarSettings.jsx';

const DND_ITEM_TYPE = "datasource-row";
const HOVER_THRESHOLD = 0.25;
const DEFAULT_DATASOURCE_SETTINGS = ["colId", "target_colId", "name_item", "value", "number_unit", "number_precision"];

function DatasourceValueSettings({
    widgetId, 
    hide_add_setting_row = false,
    setting_fields = DEFAULT_DATASOURCE_SETTINGS,
    hide_color_selection_datasource_options = ["grouped"],
    hide_color_selection_column_options = ["stacked", "clustered"],
    DatasourceSettingsRowComponent = DatasourceSettingsRow,
}){
    const dispatch = useDispatch();
    const setAttribute = useSetWidgetAttribute(widgetId);
    const headerNames = useSelector(selectWidgetEffectiveCols(widgetId));
    const headerNameOpts = headerNames.map((c) => ({
                            value: c.accessor,
                            label: c.name,
                          }));
    const columns = useSelector(state => state.dashboard.widgets[widgetId].settings?.columns) || 'single';
    const datasource_mode = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasource_mode) || 'single';
    const datasource_type = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasource_type) || 'single';
    const showGroupedDatasource = columns != "single" || datasource_mode != "single" || datasource_type != "single";
    const datasources = useSelector(state => state.dashboard.widgets[widgetId].settings?.datasources) || [];
    const hideColorSelection = hide_color_selection_column_options?.includes(columns) || hide_color_selection_datasource_options.includes(datasource_mode);
    const colWidth = Math.max(Math.floor(12 / setting_fields.length), 2);
    const [sourceIndex, setSourceIndex] = useState(null);

    const moveDatasource = (draggedIndex, targetIndex) => {
        const selectedDatasources = cloneDeep(datasources);
        const [d] = selectedDatasources.splice(draggedIndex, 1);
        selectedDatasources.splice(targetIndex, 0, d);
        setAttribute('settings.datasources', selectedDatasources)
    };

    const removeDatasource = (id) => {
        if (datasources.length > 1) {
            setAttribute('settings.datasources', datasources.filter(d => d.id !== id));
        }
    };

    const toggleDetail = (index) => {
        if (index != null) {
            dispatch(openDetail(`${SIDEBAR_SETTING_TYPES.datasource_value}-${index}`));
        } else {
            dispatch(closeDetail());
        }
        setSourceIndex(index);
    }

    const saveSettings = async () => {
        await dispatch(saveWidgetSettings());
        toggleDetail(null);
    }

    const onClose = () => {
        setSourceIndex(null);
    }
    
    // Somehow when adding dataset the scroll X happens
    const datasourceElems = datasources.map((d, index) => {
        if (datasource_mode == 'grouped' && index > 0) return null;
        return (
            <DropArea 
                key={d.id}
                accept={DND_ITEM_TYPE}
                item={{ id: d.id, index, type: DND_ITEM_TYPE }}
                onMove={moveDatasource}
                hoverThreshold={HOVER_THRESHOLD}
                render={({ dropRef, dragRef, isDragging }) => (
                    <li ref={dropRef} className={`${isDragging ? "is-dragging" : ""} widget-container list-group-item ${styles['list-group-item']} mb-1`}>
                        <DragHandle sortHandleClass={styles['sort-handle']} dragRef={dragRef} />
                        <div className={styles['list-group-item-content']}>
                            <div className={styles['list-group-item-description']}>
                                <h4 style={{overflow: 'hidden'}}>{I18n.t('datasource')} {d?.name_item || I18n.t('unnamed')}</h4>
                            </div>
                        </div>
                        <div className={styles['list-group-item-actions']}>
                            <a onClick={() => toggleDetail(index)}>
                                <FaIcon type='cog' style={{color: 'black'}} />
                            </a>
                            {datasource_mode != 'grouped' && <a onClick={(e) => removeDatasource(d.id)}>
                                <FaIcon type='trash' className='text-danger' />
                            </a>}
                        </div>
                    </li>
                )}
            />
        )
    }).filter(item => item !== null)

    return (
      <>
        { sourceIndex != null &&
            <DetailSidebarSettings saveSettings={saveSettings} onClose={onClose} sidebarId={`${SIDEBAR_SETTING_TYPES.datasource_value}-${sourceIndex}`}>
                <h4>{`Edit ${datasources[sourceIndex]?.name_item}`}</h4>
                <DatasourceSettingsRowComponent 
                    d={datasources[sourceIndex]}
                    index={sourceIndex}
                    {...{colWidth, showGroupedDatasource, hideColorSelection, widgetId, datasources, setting_fields, headerNameOpts}}
                />
            </DetailSidebarSettings>
        }
        <label>{I18n.t('datasource_values')}</label>
        <DndProvider backend={Backend}>
            <ul className='pl-0'>
                {datasourceElems}
            </ul>
        </DndProvider>
        {!hide_add_setting_row && <DatasourceAddSettingRow widgetId={widgetId} />}
      </>
    )
}

export default DatasourceValueSettings;
